import { useState } from 'react';
import { ArrowUpDown, ChevronDown, Check, ArrowUp, ArrowDown } from 'lucide-react';
import { useClickOutside } from '@/client/hooks/use-click-outside';

export type SortField = 'createdAt' | 'totalClicks' | 'shortCode';
export type SortDirection = 'asc' | 'desc';

interface SortOption {
  field: SortField;
  label: string;
}

const sortOptions: SortOption[] = [
  { field: 'createdAt', label: 'Date Created' },
  { field: 'totalClicks', label: 'Total Clicks' },
  { field: 'shortCode', label: 'Short Code' },
];

interface SortSelectorProps {
  orderBy: SortField;
  orderDir: SortDirection;
  onChange: (orderBy: SortField, orderDir: SortDirection) => void;
}

export function SortSelector({ orderBy, orderDir, onChange }: SortSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);

  // ⚡ Performance Optimization: Use shared click-outside hook instead of manual listener
  const dropdownRef = useClickOutside<HTMLDivElement>(() => setIsOpen(false), isOpen);

  const selectedOption = sortOptions.find((o) => o.field === orderBy);
  const DirectionIcon = orderDir === 'asc' ? ArrowUp : ArrowDown;

  const handleOptionClick = (field: SortField) => {
    if (field === orderBy) {
      // Toggle direction if same field
      onChange(field, orderDir === 'asc' ? 'desc' : 'asc');
    } else {
      // Default to desc for new field
      onChange(field, 'desc');
    }
    setIsOpen(false);
  };

  const toggleDirection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(orderBy, orderDir === 'asc' ? 'desc' : 'asc');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border bg-card text-sm font-medium">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="inline-flex items-center gap-2 hover:text-foreground transition-colors"
        >
          <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
          <span>{selectedOption?.label || 'Sort by'}</span>
        </button>
        <button
          onClick={toggleDirection}
          className="p-1 rounded-md hover:bg-muted transition-colors"
          title={orderDir === 'asc' ? 'Ascending' : 'Descending'}
        >
          <DirectionIcon className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-1 hover:text-foreground transition-colors"
        >
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-48 rounded-xl border bg-card shadow-xl z-50 overflow-hidden animate-fade-in">
          <div className="p-1">
            {sortOptions.map((option) => (
              <button
                key={option.field}
                onClick={() => handleOptionClick(option.field)}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-sm rounded-lg transition-colors ${
                  orderBy === option.field
                    ? 'bg-primary/10 text-primary'
                    : 'hover:bg-muted text-foreground'
                }`}
              >
                <span>{option.label}</span>
                {orderBy === option.field && (
                  <div className="flex items-center gap-1">
                    <DirectionIcon className="h-3.5 w-3.5" />
                    <Check className="h-4 w-4" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
